import type { QueryClient } from '@tanstack/react-query';

import { isCrmListKey } from '../query/queries';

interface SentinelPageLike {
  ids?: string[];
}
interface InfiniteLike {
  pages?: SentinelPageLike[];
}

/**
 * Invalidate every CRM list sentinel whose cached id list references any evicted
 * body. Bodies live only in the store; a sentinel that still points at an evicted
 * id would render a missing row (useCrmObjectList silently drops it, no self-heal),
 * so it must be invalidated so the list refetches and re-hydrates the store.
 *
 * Uses invalidateQueries ONLY (async, refetchType:'active' default) — never
 * a synchronous refetch API, which would refetch immediately and risk a
 * re-entrant eviction loop.
 */
export function invalidateListSentinelsForEvictedIds(
  queryClient: QueryClient,
  evictedIds: readonly string[]
): void {
  if (evictedIds.length === 0) {
    return;
  }
  const evicted = new Set(evictedIds);
  for (const query of queryClient.getQueryCache().findAll()) {
    if (!isCrmListKey(query.queryKey as readonly unknown[])) {
      continue;
    }
    const data = query.state.data as
      | (SentinelPageLike & InfiniteLike)
      | undefined;
    if (!data) {
      continue;
    }
    const ids: string[] =
      data.ids ?? (data.pages ? data.pages.flatMap(p => p.ids ?? []) : []);
    if (ids.length === 0) {
      continue;
    }
    if (ids.some(id => evicted.has(id))) {
      void queryClient.invalidateQueries({
        queryKey: query.queryKey,
        exact: true,
      });
    }
  }
}
